<template>
    <div class="w-100 d-flex flex-column h-100">
        <div
            v-if="showTabs"
            v-bind:class="containerClasses">
            <div
                v-for="(tab, index) in visibleTabs"
                v-bind:id="tab.tabId"
                v-bind:key="index"
                v-bind:ref="tab.tabId + 'header'"
                v-bind:class="headerClasses(tab)"
                v-bind:data-test-role="tab.dataTestRole + 'header'"
                class="tab-header-item px-2 py-1 fw-bold"
                v-on:click="setActiveTab(tab)">
                <pc-bs-icon
                    v-if="tab.icon"
                    v-bind:name="tab.icon"
                    class="text-primary"
                    style="min-width: 1rem" />
                {{ tab.title }}
            </div>
        </div>
        <slot />
    </div>
</template>

<script>
    import { safeSetItem } from "Util/util";

    const HASH_REGEX = /#(\w+)/g;

    export default {
        provide() {
            return { pcTabs: this };
        },
        inject: {
            useEmbed: { default: false }
        },
        props: {
            saveState: { type: Boolean, required: false, default: false },
            stateKey: { type: String, required: false, default: null },

            // Option to remove tabs entirely if no other options. Useful for dynamic tabs.
            hideSingle: { type: Boolean, required: false, default: false },
            textCapitalize: { type: Boolean, required: false, default: true },
            hideBorder: { type: Boolean, required: false, default: false },
            headerContainerClass: { type: Object, required: false, default: null }
        },
        emits: ["setActiveTab"],
        data: function () {
            return {
                tabs: [],
                currentTab: null,
                tabsAreInitialized: false
            };
        },
        computed: {
            showTabs() {
                return !this.hideSingle || this.visibleTabs.length > 1;
            },
            visibleTabs() {
                return this.tabs.filter((t) => t.isShown);
            },
            containerClasses() {
                return {
                    ...this.headerContainerClass,
                    "d-flex": true,
                    "w-100": true,
                    "flex-wrap": true,
                    "border-bottom": !this.hideBorder,
                    "border-light": !this.hideBorder,
                    "mb-2": true,
                    "pb-2": true
                };
            }
        },
        watch: {
            // Handle case where no tab is visible initially when mounted code executes;
            // If a visible tab is added after that and we haven't already selected a tab,
            // select the first visible tab.
            visibleTabs() {
                // If we already have a tab selected, do nothing
                // also if we're still in the inital mounting phase
                if (this.currentTab || !this.tabsAreInitialized) {
                    return;
                }
                if (this.visibleTabs?.length > 0) {
                    this.setActiveTab(this.visibleTabs[0]);
                }
            }
        },
        mounted() {
            this.tabsAreInitialized = true;
            if (this.visibleTabs.length > 0) {
                let activeTab = this.visibleTabs[0];
                if (this.saveState && this.stateKey) {
                    let savedState = localStorage.getItem(this._getLocalStorageKey());
                    savedState = JSON.parse(savedState);
                    if (savedState) {
                        const savedTab = this.visibleTabs.find((t) => t.title === savedState.title);
                        activeTab = savedTab || activeTab;
                    }
                }

                activeTab = this.getActiveTabByUrl() ?? activeTab;

                this.setActiveTab(activeTab);
            }
        },
        methods: {
            // used by child tabs to register themselves to parent.
            // Do not change without also fixing all callers in pc-tab
            registerTab(tab) {
                this.tabs.push(tab);
            },
            _getLocalStorageKey() {
                const userId = this.$app.LoggedInUser.PublicId;
                const path = window.location.pathname + this.$app.ProgramId;
                return `pc-tabs_${this.stateKey}_${path}_${userId}`;
            },
            setActiveTab(newTab) {
                if (newTab !== this.currentTab) {
                    if (this.saveState && this.stateKey) {
                        safeSetItem(this._getLocalStorageKey(), JSON.stringify({ title: newTab.title }));
                    }
                    if (this.currentTab) {
                        // _selected non reactive prop is used to immediately show the new tab as selected
                        newTab._selected = true;
                        this.currentTab.setAfterLeaveCallback(() => {
                            newTab.selected = true;
                        });
                        this.currentTab.selected = false;
                        this.currentTab._selected = false;
                    } else {
                        newTab.selected = true;
                    }
                    this.currentTab = newTab;

                    // emit the id of the tab currently selected
                    // used for setting the support center url for pages with different support center urls for each tab on the page
                    this.$emit("setActiveTab", this.currentTab.tabId);
                }
            },
            getActiveTabByUrl() {
                if (this.useEmbed) {
                    return this.visibleTabs[0];
                }
                const currentUrl = this.$app.getHref();
                const normalizedMatch = this.getNormalizedHashRegexMatch(currentUrl);
                if (normalizedMatch) {
                    const newActiveTab = this.visibleTabs.find((t) => t.tabId?.toLowerCase() === normalizedMatch);
                    return newActiveTab;
                }
            },
            getNormalizedHashRegexMatch(currentUrl) {
                const regexMatches = currentUrl.match(HASH_REGEX);
                if (regexMatches) {
                    const match = regexMatches[0];
                    let normalizedMatch = match.replace("#", "");
                    normalizedMatch = normalizedMatch.toLowerCase();
                    return normalizedMatch;
                }
            },
            headerClasses(tab) {
                return {
                    selected: tab.selected || tab._selected,
                    "text-capitalize": this.textCapitalize,
                    "tab-header-item-multiple": this.visibleTabs.length > 1
                };
            }
        }
    };
</script>

<style lang="scss">
    .tab-header-item {
        &.selected {
            border-bottom: solid 6px var(--bs-primary);
        }
    }

    .tab-header-item-multiple {
        cursor: pointer;
        &:hover {
            color: var(--bs-primary-600);
        }
    }
</style>
